import React, { useRef, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Chip,
  Container,
  IconButton,
  Typography
} from '@mui/material'
import {
  ChevronLeft,
  ChevronRight,
  ArrowForward
} from '@mui/icons-material'
import PropertyCard from '../../../../components/PropertyCard/PropertyCard'

export const BoostSection = ({
  title,
  subtitle,
  icon,
  color,
  properties,
  chipLabel,
  seeMoreLink = '/imoveis'
}) => {
  const navigate = useNavigate()
  const scrollRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    checkScroll()
    const el = scrollRef.current
    if (!el) return
    const observer = new ResizeObserver(checkScroll)
    observer.observe(el)
    return () => observer.disconnect()
  }, [checkScroll, properties])

  if (!properties || properties.length === 0) return null

  const scroll = (direction) => {
    const el = scrollRef.current
    if (!el) return
    const cardWidth = el.querySelector('[data-card]')?.offsetWidth || 200
    const gap = 16
    const distance = (cardWidth + gap) * 2
    const target = el.scrollLeft + (direction === 'left' ? -distance : distance)

    el.scrollTo({
      left: target,
      behavior: 'smooth'
    })
  }

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth='xl'>
        {/* Header — title left, arrows right (Zillow style) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {icon && (
              <Box
                sx={{
                  bgcolor: `${color}15`,
                  p: 1,
                  borderRadius: 1.5,
                  display: 'flex'
                }}
              >
                {icon}
              </Box>
            )}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='h5' sx={{ fontWeight: 700 }}>
                  {title}
                </Typography>
                {chipLabel && (
                  <Chip
                    label={chipLabel}
                    size='small'
                    sx={{
                      bgcolor: color,
                      color: 'white',
                      fontWeight: 600,
                      height: 22,
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Box>
              {subtitle && (
                <Typography variant='body2' color='text.secondary'>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Navigation arrows */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              size='small'
              sx={{
                border: '1px solid',
                borderColor: canScrollLeft ? 'grey.400' : 'grey.200',
                color: canScrollLeft ? 'text.primary' : 'grey.300',
                width: 36,
                height: 36,
                '&:hover': {
                  bgcolor: 'grey.100',
                  borderColor: 'grey.500'
                }
              }}
            >
              <ChevronLeft fontSize='small' />
            </IconButton>
            <IconButton
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              size='small'
              sx={{
                border: '1px solid',
                borderColor: canScrollRight ? 'grey.400' : 'grey.200',
                color: canScrollRight ? 'text.primary' : 'grey.300',
                width: 36,
                height: 36,
                '&:hover': {
                  bgcolor: 'grey.100',
                  borderColor: 'grey.500'
                }
              }}
            >
              <ChevronRight fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        {/* Carousel */}
        <Box
          ref={scrollRef}
          onScroll={checkScroll}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            scrollSnapType: 'x proximity',
            pb: 1,
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none'
          }}
        >
          {properties.map((property) => (
            <Box
              key={property.id}
              data-card
              sx={{
                flex: '0 0 auto',
                width: {
                  xs: '75%',
                  sm: 'calc(50% - 8px)',
                  md: 'calc(25% - 12px)'
                },
                scrollSnapAlign: 'start'
              }}
            >
              <PropertyCard property={property} onFavoriteToggle={undefined} />
            </Box>
          ))}

          {/* "Ver mais" card */}
          <Box
            data-card
            onClick={() => navigate(seeMoreLink)}
            sx={{
              flex: '0 0 auto',
              width: {
                xs: '60%',
                sm: 'calc(50% - 8px)',
                md: 'calc(25% - 12px)'
              },
              scrollSnapAlign: 'start',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 280,
              borderRadius: 2,
              border: '1.5px solid',
              borderColor: 'grey.300',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'grey.50',
                '& .see-more-text': { color: 'primary.dark' },
                '& .see-more-icon': { transform: 'translateX(4px)' }
              }
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                className='see-more-text'
                variant='subtitle1'
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'color 0.2s'
                }}
              >
                Ver mais
                <ArrowForward
                  className='see-more-icon'
                  sx={{ fontSize: 20, transition: 'transform 0.2s' }}
                />
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}
